import { Button } from '@mui/material';
import { Editor } from '@tinymce/tinymce-react';
import { useState } from 'react';

const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

interface IProps {
  content: string;
  loading?: boolean;
  onSave: (content: string) => void;
}

export function EditContentModal({
  content: initialState,
  loading,
  onSave,
}: IProps) {
  const [content, setContent] = useState(initialState);

  const handleSaveChanges = () => {
    onSave(content);
  };

  return (
    <>
      <Editor
        apiKey={TINYMCE_API_KEY}
        value={content}
        onEditorChange={(e) => setContent(e)}
        init={{
          height: 500,
          menubar: true, // Enable menubar
          branding: false,
          statusbar: false,
          plugins:
            'advlist anchor autolink codesample fullscreen help image tinydrive ' +
            'lists link media preview searchreplace table template visualblocks wordcount ' +
            'paste',
          toolbar:
            'undo redo | formatselect | ' +
            'bold italic underline superscript subscript | backcolor | ' +
            'alignleft aligncenter alignright alignjustify | ' +
            'bullist numlist outdent indent | removeformat | help code',
          // Add menu configuration to include font size options
          menu: {
            format: { 
              title: 'Format', 
              items: 'bold italic underline | formats | fontsize fontfamily | removeformat' 
            }
          },
          // Custom block formats for different text sizes
          block_formats: 'Paragraph=p; Small Text=h6; Medium Text=h5; Large Text=h4; Extra Large=h3; Huge=h2; Giant=h1',
          entity_encoding: 'raw',
          valid_elements: '*[*]',
          extended_valid_elements:
            'sup,sub,span[*],div[*],p[*],h1[*],h2[*],h3[*],h4[*],h5[*],h6[*],a[*]',
          forced_root_block: 'p',
          paste_as_text: false,
          paste_data_images: false,
          paste_webkit_styles: 'font-weight font-style color background-color',
          paste_remove_styles_if_webkit: true,
          paste_merge_formats: true,
          paste_postprocess: (plugin, args) => {
            args.node.innerHTML = args.node.innerHTML
              .replace(/([⁰¹²³⁴⁵⁶⁷⁸⁹])/g, (match) => {
                return `<sup>${match.normalize('NFKC')}</sup>`;
              })
              .replace(/([₀₁₂₃₄₅₆₇₈₉])/g, (match) => {
                return `<sub>${match.normalize('NFKC')}</sub>`;
              });
          },
          content_style:
            'body { font-family: Helvetica, Arial, sans-serif; font-size:14px; line-height:1.6; }' +
            'h1 { font-size: 32px !important; font-weight: bold; }' +
            'h2 { font-size: 28px !important; font-weight: bold; }' +
            'h3 { font-size: 24px !important; font-weight: bold; }' +
            'h4 { font-size: 20px !important; font-weight: bold; }' +
            'h5 { font-size: 16px !important; font-weight: bold; }' +
            'h6 { font-size: 12px !important; font-weight: bold; }' +
            'p { font-size: 14px !important; margin-bottom: 1em; }',
        }}
      />
      <Button
        disabled={loading}
        sx={{
          marginTop: '30px',
          width: '100%',
        }}
        type="submit"
        variant="contained"
        onClick={handleSaveChanges}
      >
        Save Changes
      </Button>
    </>
  );
}